/**
 * UserProjectCreatePage
 * [UC-042] Project creation with eligibility check and submission
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface EligibilityData {
  eligibleForNewProject: boolean;
  hasActiveSubscription: boolean;
  hasSufficientCredits: boolean;
  isFirstProject: boolean;
}

export function UserProjectCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(true);
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  // Confirm modal — once they hit Create we want to slow them down and
  // make sure they understand the description IS the spec. After this
  // the only way to change anything is a change request.
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Description textarea autosizes via CSS `field-sizing: content` — the
  // browser adjusts height to content natively, without any JS measurement
  // or height-toggling. That avoids the layout thrash the previous JS
  // autosizer caused, which was nudging window scroll on each keystroke.

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      setIsCheckingEligibility(true);
      const response = await api.get('/users/me/subscription');

      // The eligibility data is in PaginatedResponse.items
      const items = (response as any).items || [];
      const eligibilityData = items.find((item: any) =>
        item.hasOwnProperty('eligibleForNewProject')
      );

      setEligibility(eligibilityData);
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      toast.error(t('userProjectCreate.errors.eligibilityCheckFailed'));
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error(t('userProjectCreate.validation.nameRequired'));
      return false;
    }
    if (!formData.description.trim()) {
      toast.error(t('userProjectCreate.validation.descriptionRequired'));
      return false;
    }
    return true;
  };

  // Two-step submit: validate locally, then surface the confirm modal
  // so the user knows their description becomes the spec for the
  // clickable preview and any later edits go through change requests.
  const handleStartSubmit = () => {
    if (!validateForm()) return;
    if (!eligibility?.eligibleForNewProject) {
      toast.error(t('userProjectCreate.errors.notEligible'));
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    try {
      setIsLoading(true);

      const response = await api.post('/projects', {
        name: formData.name,
        description: formData.description
      });

      toast.success(t('userProjectCreate.success.projectCreated'));

      // Navigate to project detail or dashboard
      navigate('/user-dashboard');

    } catch (error: any) {
      console.error('Failed to create project:', error);

      if (error.response?.status === 402) {
        toast.error(t('userProjectCreate.errors.insufficientCredits'));
      } else {
        toast.error(t('userProjectCreate.errors.creationFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingEligibility) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-gray-700">{t('userProjectCreate.loading.checkingEligibility')}</span>
        </div>
      </div>
    );
  }

  if (!eligibility?.eligibleForNewProject) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <Card>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('userProjectCreate.eligibility.notEligible.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {eligibility?.isFirstProject
                ? t('userProjectCreate.eligibility.notEligible.firstProject')
                : t('userProjectCreate.eligibility.notEligible.needCreditsOrSubscription')
              }
            </p>

            <div className="space-y-3">
              {!eligibility?.hasActiveSubscription && (
                <Button
                  onClick={() => navigate('/user-subscription')}
                  className="w-full"
                >
                  {t('userProjectCreate.buttons.getSubscription')}
                </Button>
              )}

              {!eligibility?.hasSufficientCredits && (
                <Button
                  onClick={() => navigate('/user-credit-purchase')}
                  variant="outline"
                  className="w-full"
                >
                  {t('userProjectCreate.buttons.purchaseCredits')}
                </Button>
              )}

              <Button
                onClick={() => navigate('/user-dashboard')}
                variant="outline"
                className="w-full"
              >
                {t('userProjectCreate.buttons.backToDashboard')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <Card>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('userProjectCreate.title')}
          </h1>
          <p className="text-gray-600">
            {t('userProjectCreate.subtitle')}
          </p>
        </div>

        {/* Eligibility Status */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-800">
              {eligibility?.isFirstProject
                ? t('userProjectCreate.eligibility.firstProject')
                : t('userProjectCreate.eligibility.eligible')
              }
            </span>
          </div>
        </div>

        <form className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('userProjectCreate.form.name.label')}
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('userProjectCreate.form.name.placeholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('userProjectCreate.form.description.label', 'Requirements & business case')}
            </label>
            <p className="text-sm text-gray-600 mb-2">
              {t(
                'userProjectCreate.form.description.helper',
                "What you write here is treated as your requirements and business case — we'll use it as the spec to start building your clickable app preview. Be as concrete as you can about who uses the app, the main workflows, and the outcome you want."
              )}
            </p>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none [field-sizing:content]"
              rows={6}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('userProjectCreate.form.description.placeholder')}
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/user-dashboard')}
              disabled={isLoading}
            >
              {t('userProjectCreate.buttons.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleStartSubmit}
              disabled={isLoading}
            >
              {isLoading ? t('userProjectCreate.buttons.creating') : t('userProjectCreate.buttons.createProject')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Confirmation modal — surfaces the contract before we lock the
          scope: from this point on, edits go through change requests
          rather than free-form description tweaks. */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[var(--surface-raised)] rounded-xl border border-gray-200 dark:border-[var(--border-subtle)] p-5 sm:p-6 max-w-lg w-full shadow-2xl">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-[var(--text-primary)] mb-3">
              {t('userProjectCreate.confirmModal.title', 'Start building the clickable preview?')}
            </h3>
            <p className="text-sm text-gray-700 dark:text-[var(--text-secondary)] mb-4">
              {t(
                'userProjectCreate.confirmModal.body',
                "We'll use the project name and description below as the spec for your clickable app preview. After you confirm, the scope is locked — any further changes go through change requests."
              )}
            </p>
            <div className="bg-gray-50 dark:bg-[var(--surface-base)] border border-gray-200 dark:border-[var(--border-subtle)] rounded-lg p-3 mb-4 max-h-48 overflow-auto">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-[var(--text-muted)] font-semibold mb-1">
                {t('userProjectCreate.confirmModal.nameLabel', 'Project')}
              </div>
              <div className="text-sm text-gray-900 dark:text-[var(--text-primary)] font-medium mb-3 break-words">
                {formData.name || <span className="text-gray-400 italic">—</span>}
              </div>
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-[var(--text-muted)] font-semibold mb-1">
                {t('userProjectCreate.confirmModal.descriptionLabel', 'Requirements')}
              </div>
              <div className="text-sm text-gray-900 dark:text-[var(--text-primary)] whitespace-pre-wrap break-words">
                {formData.description || <span className="text-gray-400 italic">—</span>}
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg p-3 mb-5 flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-amber-900 dark:text-amber-100">
                {t(
                  'userProjectCreate.confirmModal.warning',
                  'After this point, modifications are only possible through change requests.'
                )}
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={isLoading}
              >
                {t('userProjectCreate.confirmModal.back', 'Back to edit')}
              </Button>
              <Button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={isLoading}
              >
                {isLoading
                  ? t('userProjectCreate.buttons.creating')
                  : t('userProjectCreate.confirmModal.confirm', 'Yes, start building')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProjectCreatePage;
